import React from 'react';
import { useRecoilValue } from 'recoil';
import { Card } from '../../components/Card';
import { CardTitle } from '../../components/CardTitle';
import { useSupportModal } from '../../hooks/actions/useSupportModal';
import { Icon } from '../../icons/Icon';
import { HoneyloveWindow } from '../../models/HoneyloveWindow';
import { environmentContextState } from '../../recoil/atoms';
import { Button } from '../../root/components/buttons/Button';
import { LeadGenerationForm } from '../../root/components/LeadGenerationForm';
import { Link } from '../../root/components/Link';
import { assetPathFn } from '../../util';
import styles from './MainFooterDesktop.module.scss';

declare const window: HoneyloveWindow;

export const MainFooterDesktop: React.FC = () => {
  const environmentContext = useRecoilValue(environmentContextState);
  const launchSupportModal = useSupportModal();
  const openZendeskWidget = () => {
    window?.zE('webWidget', 'show');
    window?.zE('webWidget', 'updateSettings', { webWidget: { helpCenter: { suppress: true } } });
    window?.zE('webWidget', 'open');
  };

  return (
    <>
      <Card>
        <footer role="contentinfo" className={styles.footer}>
          <div>
            <CardTitle text={'Join Our Community'} style={{ textAlign: 'left', padding: '50px 0' }} />
            <div>Get exclusive offers and product previews</div>
            <div className={styles.klaviyoInputArea}>
              <LeadGenerationForm
                placeholder="Enter your email"
                klaviyoFormKeys={environmentContext.klaviyo.newsletterFooter}
                textboxStyle={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                buttonMessage={'Subscribe'}
              />
            </div>
            <div style={{ marginTop: 24 }}>Join the Hive and start collecting loyalty points</div>
            <div>
              <Link
                buttonType="secondary"
                size="medium"
                text="Loyalty Program"
                href="/pages/loyalty"
                dataTrackLabel="loyalty-program"
                dataTrackLocation="footer"
              />
            </div>
            <div>
              <Link
                buttonType="secondary"
                size="medium"
                text="Referral Program"
                dataTrackLabel="referral-program"
                dataTrackLocation="footer"
                href="https://honeylove.referralcandy.com/"
              />
            </div>
            <div style={{ marginTop: 24 }}>Follow us</div>
            <div className={styles.socialIcons}>
              <Link dataTrackLabel="Facebook Icon" dataTrackLocation="footer" href="https://www.facebook.com/GetHoneyLove/">
                <img src={assetPathFn('facebook.png')} alt="honeylove facebook" />
              </Link>
              <Link dataTrackLabel="Instagram Icon" dataTrackLocation="footer" href="https://www.instagram.com/honeylove/">
                <img src={assetPathFn('instagram.png')} alt="honeylove instagram" />
              </Link>
              <Link dataTrackLabel="Twitter Icon" dataTrackLocation="footer" href="https://twitter.com/gethoneylove">
                <img src={assetPathFn('twitter-icon.png')} alt="honeylove twitter" />
              </Link>
              <Link dataTrackLabel="Pinterest Icon" dataTrackLocation="footer" href="https://www.pinterest.com/gethoneylove/">
                <img src={assetPathFn('pinterest.png')} alt="honeylove pinterest" />
              </Link>
              <Link dataTrackLabel="YouTube Icon" dataTrackLocation="footer" href="https://www.youtube.com/channel/UCVE8b1s_zmMUBIDbULsIqlg/videos">
                <img src={assetPathFn('youtube.png')} alt="honeylove youtube" />
              </Link>
            </div>
          </div>
          <div>
            <CardTitle text={'Customer Support'} style={{ textAlign: 'left', padding: '50px 0' }} />
            <div>
              <div className={styles.easyReturns}>
                <img src={assetPathFn('30-day-icon.png')} style={{ width: 68, height: 54, marginLeft: -18 }} alt="30 day returns" />
                <CardTitle text="Easy returns" style={{ padding: `0`, fontSize: '13px', textAlign: 'left' }} />
              </div>
              Enjoy free US shipping and 30-day returns. <br />
              Return policy
            </div>
            <div>
              <Link buttonType="secondary" size="medium" text="Returns" dataTrackLabel="Returns" dataTrackLocation="footer" href="/pages/returns" />
            </div>
            <div>
              <Link buttonType="secondary" size="medium" text="FAQs" dataTrackLabel="FAQ" dataTrackLocation="footer" href="https://help.honeylove.com" />
            </div>
            <div className={styles.getInTouch}>
              <Icon name="phone" style={{ width: '24px', color: '#888', marginRight: '10px', marginTop: '-4px' }} />
              <CardTitle text="Get In Touch" style={{ padding: `0`, fontSize: '13px', textAlign: 'left' }} />
            </div>
            <div>6am to 6pm PST every day</div>
            <Button buttonType="secondary" size="medium" text="Contact Us" onClick={launchSupportModal} noMargin={true} />
          </div>
          <div className={styles.resourceLinks}>
            <CardTitle text={'Resources'} style={{ textAlign: 'left', padding: '50px 0' }} />
            <Link dataTrackLabel="e-gift-card" dataTrackLocation="footer" href="/products/e-gift-card">
              Gift Cards
            </Link>
            <Link dataTrackLabel="Blog" dataTrackLocation="footer" href="/blogs/essays">
              Blog
            </Link>
            <Link dataTrackLabel="Reviews" dataTrackLocation="footer" href="/pages/reviews">
              Reviews
            </Link>
            <Link dataTrackLabel="How to wear" dataTrackLocation="footer" href="/pages/how-to-wear">
              How to wear
            </Link>
            <Link dataTrackLabel="Schedule a fit consultation" dataTrackLocation="footer" href="/pages/consultation">
              Schedule a fit consultation
            </Link>
            <Link dataTrackLabel="Frontline worker discount" dataTrackLocation="footer" href="/pages/frontline-workers">
              Frontline &#38; Military discount
            </Link>
            <Link dataTrackLabel="Privacy Policy" dataTrackLocation="footer" href="/pages/privacy-policy">
              Privacy policy
            </Link>
            <Link dataTrackLabel="Terms of Service" dataTrackLocation="footer" href="/pages/terms-of-service">
              Terms of service
            </Link>
            <Link dataTrackLabel="Coupons" dataTrackLocation="footer" href="/pages/coupons">
              Coupon policy
            </Link>
            <Link dataTrackLabel="Amazon" dataTrackLocation="footer" href="/pages/amazon">
              Amazon policy
            </Link>
            <Link dataTrackLabel="Sustainability" dataTrackLocation="footer" href="/pages/sustainability">
              Sustainability
            </Link>
            <Link dataTrackLabel="Affiliates" dataTrackLocation="footer" href="/pages/affiliates">
              Affiliates
            </Link>
            <Link dataTrackLabel="Accessibility" dataTrackLocation="footer" href="/pages/accessibility">
              Accessibility Statement
            </Link>
            <Button
              dataTrackLabel="Cookie Settings/CCPA"
              dataTrackLocation="footer"
              buttonType="link"
              text="Cookie Settings/CCPA"
              onClick={() => document.getElementById('ot-sdk-btn')?.click()}
            />
            <div className={styles.accessibility}>
              <Link
                dataTrackLabel="Icon & Assistive Technology"
                dataTrackLocation="footer"
                href="https://www.essentialaccessibility.com/honeylove?utm_source=honeylovehomepage&utm_medium=iconlarge&utm_term=eachannelpage&utm_content=header&utm_campaign=honeylove"
                buttonType="ghost"
              >
                <img
                  className={styles.accessibilityIcon}
                  src={assetPathFn('eA_Icon.svg')}
                  alt="This icon serves as a link to download the eSSENTIAL Accessibility assistive technology app for individuals with physical disabilities. It is featured as part of our commitment to diversity and inclusion."
                />
              </Link>
            </div>
          </div>
        </footer>
      </Card>
      <div className={styles.copyright}>Copyright 2022 Honeylove®</div>
    </>
  );
};

export default MainFooterDesktop;
